<script setup lang="ts">
import { computed } from 'vue';

import { AppLoadingDots, AppSpinner } from '../appLoaders/index';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import type {
  AppStepperButtonIconProperties,
  AppStepperButtonSize,
  AppStepperButtonState,
} from './AppStepperButton.model';

interface AppStepperButtonProps {
  state: AppStepperButtonState;
  size: AppStepperButtonSize;
  icon?: AppStepperButtonIconProperties;
  wide?: boolean;
}

const props = defineProps<AppStepperButtonProps>();

interface AppStepperButtonEmits {
  (eventName: 'click', event: MouseEvent): void;
}

const emit = defineEmits<AppStepperButtonEmits>();

const iconSize = computed(() => {
  if (props.icon && props.icon.size) {
    return props.icon.size;
  } else if (props.size === 'small') {
    return '18';
  } else {
    return '24';
  }
});

function onClick(event: MouseEvent): void {
  if (props.state) {
    emit('click', event);
  }
}
</script>

<template>
  <button
    class="app-button"
    :disabled="props.state === 'disabled'"
    :class="[[props.size], [props.state], props.wide ? 'wide' : undefined]"
    @click="onClick"
  >
    <AppSpinner v-if="props.state === 'loadingSpinner'" />
    <AppLoadingDots v-else-if="props.state === 'loadingDots'" type="white" />
    <template v-else>
      <AppSvgIcon v-if="props.icon" :name="props.icon.name" :size="iconSize" />
    </template>
  </button>
</template>

<style scoped lang="scss">
@import '../../styles/_media';

@mixin disabledState {
  &.loadingDots,
  &.loadingSpinner,
  &:disabled {
    background-color: var(--color-white);
    color: var(--color-neutral-30);
    cursor: default;
    pointer-events: none;
    border: 1px solid var(--color-neutral-30);
  }
}

.app-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-4);
  border-radius: var(--border-radius-8);
  white-space: nowrap;
  transition-property: background-color, transform;
  transition-duration: var(--transition-time-medium);
  transition-timing-function: ease-out;

  background-color: var(--color-white);
  color: var(--color-neutral-70);
  border: 1px solid var(--color-neutral-40);
  text-decoration: none;

  @include disabledState;

  @include on-hover {
    background-color: var(--color-neutral-10);
    cursor: pointer;
  }

  &:focus {
    border: 1px solid var(--color-neutral-70);
  }

  &:active {
    transform: matrix(0.95, 0, 0, 0.95, 0, 0);
  }

  &.wide {
    width: 100%;
  }

  // Sizes
  &.small {
    --size-small: 36px;
    height: var(--size-small);
    width: var(--size-small);
    padding: 0;
  }

  &.medium {
    --size-medium: 44px;
    height: var(--size-medium);
    width: var(--size-medium);
  }

  &.large {
    --size-large: 56px;
    height: var(--size-large);
    width: var(--size-large);
    padding: 0;
  }
}
</style>
